import { useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import { Calendar, Plus, Trash2, Edit2, X, Check, Clock, Video, ExternalLink } from "lucide-react";
import { ConfirmModal } from "../../components/ConfirmModal";

/* ---------- TYPES ---------- */

interface TrainingSession {
  id: string;
  title: string;
  date: string;
  time: string;
  platform: string | null;
  meeting_link: string | null;
  password: string | null;
  trainer_name: string;
  additional_info: string | null;
  author_id: string;
}

/* ---------- COMPONENT ---------- */

export function UpcomingSessionsTab() {
  const [sessions, setSessions] = useState<TrainingSession[]>([]);
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    title: "",
    date: "",
    time: "",
    platform: "Zoom",
    meeting_link: "",
    password: "",
    trainer_name: "",
    additional_info: "",
  });

  useEffect(() => {
    loadProfile();
    loadSessions();
  }, []);

  /* ---------- PROFILE ---------- */

  async function loadProfile() {
    const { data } = await supabase.auth.getUser();
    const userId = data.user?.id;
    if (!userId) return;

    const { data: p } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .single();

    setProfile(p);
  }

  function canManage() {
    if (!profile) return false;
    if (profile.role === "superadmin") return true;
    if (profile.role === "admin" && profile.admin_level >= 2) return true;
    return false;
  }

  /* ---------- LOAD ---------- */

  async function loadSessions() {
    setLoading(true);

    const { data } = await supabase
      .from("trainings")
      .select("*")
      .eq("type", "upcoming")
      .order("date", { ascending: true })
      .order("time", { ascending: true });

    if (data) setSessions(data);

    setLoading(false);
  }

  /* ---------- CREATE / UPDATE ---------- */

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canManage()) return;

    const { data } = await supabase.auth.getUser();
    const userId = data.user?.id;

    if (editingId) {
  const { data, error } = await supabase
    .from("trainings")
    .update(formData)
    .eq("id", editingId)
    .select()
    .single();

  if (!error && data) {
    setSessions(prev =>
      prev.map(s => (s.id === editingId ? data : s))
    );
  }
} else {
  const { data, error } = await supabase
    .from("trainings")
    .insert({
      ...formData,
      type: "upcoming",
      author_id: userId,
    })
    .select()
    .single();

  if (!error && data) {
    setSessions(prev => [data, ...prev]);
  }
}

    setEditingId(null);
    setShowForm(false);
    setFormData({
      title: "",
      date: "",
      time: "",
      platform: "Zoom",
      meeting_link: "",
      password: "",
      trainer_name: "",
      additional_info: "",
    });

    
  }

  /* ---------- DELETE ---------- */

  async function deleteSession(id: string) {
  const { error } = await supabase
    .from("trainings")
    .delete()
    .eq("id", id);

  if (!error) {
    setSessions(prev => prev.filter(s => s.id !== id));
  }

  setDeleteConfirmId(null);
}

  /* ---------- EDIT ---------- */

  function startEdit(session: TrainingSession) {
    setEditingId(session.id);
    setFormData({
      title: session.title,
      date: session.date,
      time: session.time,
      platform: session.platform || "Zoom",
      meeting_link: session.meeting_link || "",
      password: session.password || "",
      trainer_name: session.trainer_name,
      additional_info: session.additional_info || "",
    });
    
  }

  function cancelEdit() {
    setEditingId(null);
    setShowForm(false);
  }

  if (loading) return <div className="text-center py-12 text-gray-500">Loading...</div>;

  return (
    <div className="space-y-6">

      <div className="flex justify-between">
        <h1 className="text-2xl font-bold">Upcoming Training Sessions</h1>
        {canManage() && !showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 bg-nhs-blue text-white px-4 py-2 rounded-lg"
          >
            <Plus className="h-4 w-4" />
            Add Session
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white p-6 rounded-xl shadow grid gap-4 md:grid-cols-2">
          <input required placeholder="Title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            className="border p-2 rounded" />

          <select
            value={formData.platform}
            onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
            className="border p-2 rounded">
            <option>Zoom</option>
            <option>Teams</option>
            <option>Google Meet</option>
            <option>Other</option>
          </select>

          <input type="date" required
            value={formData.date}
            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
            className="border p-2 rounded" />

          <input type="time" required
            value={formData.time}
            onChange={(e) => setFormData({ ...formData, time: e.target.value })}
            className="border p-2 rounded" />

          <input required placeholder="Meeting Link"
            value={formData.meeting_link}
            onChange={(e) => setFormData({ ...formData, meeting_link: e.target.value })}
            className="border p-2 rounded md:col-span-2" />

          <input placeholder="Password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            className="border p-2 rounded md:col-span-2" />

          <input required placeholder="Trainer Name"
            value={formData.trainer_name}
            onChange={(e) => setFormData({ ...formData, trainer_name: e.target.value })}
            className="border p-2 rounded md:col-span-2" />

          <textarea placeholder="Additional Info"
            value={formData.additional_info}
            onChange={(e) => setFormData({ ...formData, additional_info: e.target.value })}
            className="border p-2 rounded md:col-span-2" />

          <button type="submit" className="bg-nhs-blue text-white px-4 py-2 rounded">
            {editingId ? "Update" : "Create"}
          </button>
        </form>
      )}

      {sessions.map((session) => (
  <div
    key={session.id}
    className="bg-nhs-blue text-white p-5 rounded-xl shadow-md flex justify-between items-start"
  >
    <div className="flex-1">

      {editingId === session.id ? (

        /* ================= INLINE EDIT MODE ================= */

        <div className="space-y-3">

          <input
            value={formData.title}
            onChange={(e) =>
              setFormData({ ...formData, title: e.target.value })
            }
            className="w-full border rounded px-3 py-2 text-sm text-black"
          />

          <div className="flex gap-3">
            <input
              type="date"
              value={formData.date}
              onChange={(e) =>
                setFormData({ ...formData, date: e.target.value })
              }
              className="border rounded px-3 py-2 text-sm text-black"
            />

            <input
              type="time"
              value={formData.time}
              onChange={(e) =>
                setFormData({ ...formData, time: e.target.value })
              }
              className="border rounded px-3 py-2 text-sm text-black"
            />
          </div>

          <div className="flex gap-3 mt-2">
            <button
              onClick={handleSubmit}
              className="bg-white text-nhs-blue px-3 py-1 rounded font-semibold"
            >
              Save
            </button>

            <button
              onClick={cancelEdit}
              className="bg-white/30 text-white px-3 py-1 rounded"
            >
              Cancel
            </button>
          </div>

        </div>

      ) : (

        /* ================= DISPLAY MODE ================= */

        <>
          <h3 className="font-semibold text-lg">
            {session.title}
          </h3>

          <div className="text-sm mt-1 text-white">
            {session.date} • {session.time}
          </div>

          {session.platform && (
            <div className="text-sm mt-1 text-white">
              Platform: {session.platform}
            </div>
          )}

          {session.meeting_link && (
            <div className="text-sm mt-1">
              <a
                href={session.meeting_link}
                target="_blank"
                rel="noopener noreferrer"
                className="text-white text-base font-semibold underline hover:text-blue-200 transition-colors"
              >
                Join Meeting
              </a>
            </div>
          )}

          {session.password && (
            <div className="text-sm mt-1 text-white">
              Password: {session.password}
            </div>
          )}

          {session.trainer_name && (
            <div className="text-sm mt-1 text-white">
              Trainer: {session.trainer_name}
            </div>
          )}

          {session.additional_info && (
            <div className="text-sm mt-1 text-white">
              {session.additional_info}
            </div>
          )}
        </>
      )}
    </div>

    {canManage() && (
      <div className="flex gap-2 ml-4">
        {editingId !== session.id && (
          <>
            <button
              onClick={() => startEdit(session)}
              className="hover:text-blue-200 transition-colors"
            >
              <Edit2 size={16} />
            </button>

            <button
              onClick={() => setDeleteConfirmId(session.id)}
              className="hover:text-red-300 transition-colors"
            >
              <Trash2 size={16} />
            </button>
          </>
        )}
      </div>
    )}
  </div>
))}

      <ConfirmModal
        open={deleteConfirmId !== null}
        title="Delete Session"
        message="Are you sure you want to delete this session?"
        variant="danger"
        confirmText="Delete"
        onConfirm={() => deleteConfirmId && deleteSession(deleteConfirmId)}
        onCancel={() => setDeleteConfirmId(null)}
      />
    </div>
  );
}